// Gemeinsame Chart-Analyse-Helfer (EMA, Support/Widerstand) fuer
// MarketdataChartTile.js - dieselbe Logik wie in MarketdataDetailPanel.js,
// hier ausgelagert, damit die kleinen Chart-Kacheln im Charts-Modul
// (MarketdataCharts.js) dieselben Indikatoren zeigen koennen, ohne den Code
// zu duplizieren.

// Exponentiell gleitender Durchschnitt ueber "period" Perioden - haengt pro
// Kerze ein Feld "ema<period>" an (null, solange nicht genug Kerzen fuer eine
// halbwegs eingeschwungene Berechnung vorliegen).
export function computeEma(chartData, period) {
	const key = 'ema' + period;
	if (!chartData || chartData.length === 0) {
		return { data: chartData || [], hasValues: false };
	}
	const alpha = 2 / (period + 1);
	let ema = null;
	let seededCount = 0;
	const data = chartData.map((row) => {
		if (row.close == null) {
			return { ...row, [key]: ema };
		}
		ema = ema == null ? row.close : row.close * alpha + ema * (1 - alpha);
		seededCount++;
		return { ...row, [key]: seededCount >= period ? ema : null };
	});
	return { data, hasValues: data.some((row) => row[key] != null) };
}

// Support/Resistance-Erkennung (Chat vom 2026-08-11 neu geschrieben, alter
// Ansatz lieferte "Unsinn": Level direkt am aktuellen Kurs, Session-Hoch/-Tief
// fehlten). Grundprinzipien echter Charttechnik:
//   - Widerstand aus Swing-HOCHS (HIGH), Unterstuetzung aus Swing-TIEFS (LOW) -
//     NICHT aus Schlusskursen (die verfehlen die tatsaechlichen Extrempunkte).
//   - Session-Hoch/-Tief immer als Kandidaten (die wichtigsten Tages-Level).
//   - MINDESTABSTAND zum aktuellen Kurs: ein Level 1-2 Ticks weg ist kein
//     Support/Widerstand, sondern Rauschen. Der Abstand ist an die Datenspanne
//     gekoppelt und skaliert damit automatisch mit der Zeitebene (Intraday vs.
//     Tage). Liegt der Kurs am Extrem (Ausbruch), kommt auf der Seite bewusst
//     KEIN Level (statt eines Fake-Levels am Preis).
export function computeSupportResistance(chartData, currentPrice, levelCount = 3, window = 3) {
	const rows = (chartData || []).filter((r) => r && r.close != null);
	if (rows.length < window * 2 + 1 || currentPrice == null) {
		return { supports: [], resistances: [] };
	}
	const hi = (r) => (r.high != null ? r.high : r.close);
	const lo = (r) => (r.low != null ? r.low : r.close);

	const pivotHighs = [];
	const pivotLows = [];
	for (let i = window; i < rows.length - window; i++) {
		const h = hi(rows[i]);
		const l = lo(rows[i]);
		let isHigh = true;
		let isLow = true;
		for (let j = i - window; j <= i + window; j++) {
			if (j === i) continue;
			if (hi(rows[j]) > h) isHigh = false;
			if (lo(rows[j]) < l) isLow = false;
		}
		if (isHigh) pivotHighs.push(h);
		if (isLow) pivotLows.push(l);
	}
	// Session-Extrema immer mitnehmen (die markantesten Level).
	const sessionHigh = Math.max(...rows.map(hi));
	const sessionLow = Math.min(...rows.map(lo));
	pivotHighs.push(sessionHigh);
	pivotLows.push(sessionLow);

	// Mindestabstand vom Kurs UND zwischen den Leveln: 5 % der Datenspanne
	// (skaliert mit der Zeitebene), mind. ein kleiner Bruchteil des Kurses.
	const range = sessionHigh - sessionLow;
	const minDist = Math.max(range * 0.05, currentPrice * 0.0005);

	const resistCands = [...new Set(pivotHighs.filter((v) => v - currentPrice >= minDist))].sort((a, b) => a - b);
	const supportCands = [...new Set(pivotLows.filter((v) => currentPrice - v >= minDist))].sort((a, b) => b - a);

	function pickDistinctLevels(sortedCandidates) {
		const accepted = [];
		for (const candidate of sortedCandidates) {
			if (accepted.every((level) => Math.abs(level - candidate) >= minDist)) {
				accepted.push(candidate);
				if (accepted.length >= levelCount) break;
			}
		}
		return accepted;
	}

	return {
		supports: pickDistinctLevels(supportCands),
		resistances: pickDistinctLevels(resistCands),
	};
}

// Letzter (eingeschwungener) EMA-Wert ueber "period" - nutzt computeEma und
// gibt den letzten nicht-null-Wert zurueck (oder null).
export function lastEma(chartData, period) {
	const { data } = computeEma(chartData, period);
	for (let i = data.length - 1; i >= 0; i--) {
		if (data[i]['ema' + period] != null) {
			return data[i]['ema' + period];
		}
	}
	return null;
}

// RSI (Wilder-Glaettung, Standardperiode 14) - identische Logik wie bisher in
// MarketdataDetailPanel.js, hierher ausgelagert. Gibt den letzten RSI-Wert
// zurueck (oder null bei zu wenig Daten).
export function computeRsi(chartData, period = 14) {
	const closes = (chartData || []).filter((row) => row && row.close != null);
	if (closes.length < period + 1) {
		return null;
	}
	let avgGain = 0;
	let avgLoss = 0;
	let rsi = null;
	for (let i = 1; i < closes.length; i++) {
		const diff = closes[i].close - closes[i - 1].close;
		const gain = diff > 0 ? diff : 0;
		const loss = diff < 0 ? -diff : 0;
		if (i <= period) {
			avgGain += gain / period;
			avgLoss += loss / period;
		} else {
			avgGain = (avgGain * (period - 1) + gain) / period;
			avgLoss = (avgLoss * (period - 1) + loss) / period;
		}
		if (i >= period) {
			rsi = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
		}
	}
	return rsi;
}

// MACD (12/26/9) - gibt die letzten Werte inkl. des vorherigen Histogramms
// zurueck (fuer die Crossover-Erkennung), oder null bei zu wenig Daten.
export function computeMacd(chartData, fast = 12, slow = 26, signalPeriod = 9) {
	const closes = (chartData || []).filter((row) => row && row.close != null);
	if (closes.length < slow + signalPeriod) {
		return null;
	}
	const fastAlpha = 2 / (fast + 1);
	const slowAlpha = 2 / (slow + 1);
	const signalAlpha = 2 / (signalPeriod + 1);
	let emaFast = null;
	let emaSlow = null;
	let signal = null;
	let last = null;
	let prevHistogram = null;
	for (let i = 0; i < closes.length; i++) {
		const close = closes[i].close;
		emaFast = emaFast == null ? close : close * fastAlpha + emaFast * (1 - fastAlpha);
		emaSlow = emaSlow == null ? close : close * slowAlpha + emaSlow * (1 - slowAlpha);
		const macdLine = emaFast - emaSlow;
		if (i >= slow - 1) {
			signal = signal == null ? macdLine : macdLine * signalAlpha + signal * (1 - signalAlpha);
		}
		if (i >= slow - 1 + signalPeriod) {
			if (last != null) {
				prevHistogram = last.histogram;
			}
			last = { macd: macdLine, signal, histogram: macdLine - signal };
		}
	}
	if (last == null) {
		return null;
	}
	return { ...last, prevHistogram };
}

// Candlestick-Muster der letzten (1-2) Kerzen erkennen (Chat vom 2026-08-11,
// Nutzerwunsch). Braucht OHLC (nicht nur close) - liefert das aussagekraeftigste
// Muster als { bias, label } oder null. Prioritaet: 2-Kerzen-Engulfing (stark) >
// Hammer/Shooting-Star (1 Kerze) > Doji (Unentschlossenheit, bias 0).
export function detectCandlePattern(chartData) {
	const rows = (chartData || []).filter((r) => r && r.open != null && r.close != null && r.high != null && r.low != null);
	if (rows.length < 2) {
		return null;
	}
	const c = rows[rows.length - 1];
	const p = rows[rows.length - 2];
	const range = c.high - c.low;
	if (!(range > 0)) {
		return null;
	}
	const body = Math.abs(c.close - c.open);
	const upper = c.high - Math.max(c.open, c.close);
	const lower = Math.min(c.open, c.close) - c.low;
	const cBull = c.close > c.open;
	const cBear = c.close < c.open;
	const pBull = p.close > p.open;
	const pBear = p.close < p.open;

	// Engulfing (aktuelle Kerze umschliesst den Koerper der vorigen, Gegenfarbe).
	if (cBull && pBear && c.close >= p.open && c.open <= p.close) {
		return { bias: 1, label: 'Bullische Umkehrkerze (Engulfing)' };
	}
	if (cBear && pBull && c.close <= p.open && c.open >= p.close) {
		return { bias: -1, label: 'Bärische Umkehrkerze (Engulfing)' };
	}
	// Hammer: kleiner Koerper oben, langer unterer Docht (>= 2x Koerper).
	if (body > 0 && lower >= 2 * body && upper <= body) {
		return { bias: 1, label: 'Hammer (langer Docht unten = Kaufdruck)' };
	}
	// Shooting Star: kleiner Koerper unten, langer oberer Docht.
	if (body > 0 && upper >= 2 * body && lower <= body) {
		return { bias: -1, label: 'Shooting Star (langer Docht oben = Verkaufsdruck)' };
	}
	// Doji: sehr kleiner Koerper -> Unentschlossenheit (kein Bias).
	if (body <= 0.1 * range) {
		return { bias: 0, label: 'Doji (Unentschlossenheit)' };
	}
	return null;
}

// Schwellwerte fuer die Einstiegs-Bewertung (Chat vom 2026-08-10) - bewusst
// als Konstanten, damit sie leicht nachjustierbar sind.
const ANALYSIS = {
	nearLevelPct: 0.03,   // Kurs <=3% an Unterstuetzung/Widerstand = "am Level" (Tagesbasis)
	nearLevelPctIntraday: 0.004, // enger fuer 1-Minuten-Intraday (0,4%)
	rsiOversold: 30,
	rsiOverbought: 70,
	rsiBullTrigger: 35,   // etwas lockerer fuer den Einstiegs-Trigger
	rsiBearTrigger: 65,
	minScore: 2,          // Mindest-Netto-Score fuer ein klares Long/Short-Votum
};

// Charttechnische Gesamtbewertung eines Wertpapiers (Chat vom 2026-08-10) -
// prueft Support/Widerstand, EMA50/EMA200, RSI und MACD und leitet daraus ein
// Votum ab: 'long', 'short' oder 'neutral'. Braucht TAGES-Kerzen (EMA200 =
// 200 Handelstage). currentPrice optional (sonst letzter Schlusskurs).
//
// Ein Long/Short-Votum verlangt BEIDES: eine Trendbestaetigung (Netto-Score
// der Einzelkriterien) UND einen konkreten Einstiegs-Trigger (RSI-Extrem,
// Naehe zu Unterstuetzung/Widerstand oder frischer MACD-Crossover) - reiner
// "ist im Trend" reicht bewusst NICHT als aktuelle Einstiegs-Chance.
export function analyzeSecurity(chartData, currentPrice) {
	const closes = (chartData || []).filter((row) => row && row.close != null);
	const price = currentPrice != null ? currentPrice : (closes.length ? closes[closes.length - 1].close : null);
	if (price == null || closes.length < 30) {
		return { ok: false };
	}

	const ema50 = lastEma(closes, 50);
	const ema200 = lastEma(closes, 200);
	const rsi = computeRsi(closes, 14);
	const macd = computeMacd(closes, 12, 26, 9);
	const { supports, resistances } = computeSupportResistance(closes, price, 3);
	const nearestSupport = supports.length ? supports[0] : null;
	const nearestResistance = resistances.length ? resistances[0] : null;
	const supportDistPct = nearestSupport != null ? (price - nearestSupport) / price : null;
	const resistanceDistPct = nearestResistance != null ? (nearestResistance - price) / price : null;

	const criteria = [];
	if (ema50 != null && ema200 != null) {
		criteria.push({ key: 'ema', bias: ema50 >= ema200 ? 1 : -1, label: ema50 >= ema200 ? 'EMA50 über EMA200 (Aufwärtstrend)' : 'EMA50 unter EMA200 (Abwärtstrend)' });
	}
	if (ema200 != null) {
		criteria.push({ key: 'ema200', bias: price >= ema200 ? 1 : -1, label: price >= ema200 ? 'Kurs über EMA200' : 'Kurs unter EMA200' });
	}
	if (rsi != null) {
		let bias = 0;
		let label = 'RSI neutral (' + rsi.toFixed(0) + ')';
		if (rsi <= ANALYSIS.rsiOversold) { bias = 1; label = 'RSI überverkauft (' + rsi.toFixed(0) + ')'; }
		else if (rsi >= ANALYSIS.rsiOverbought) { bias = -1; label = 'RSI überkauft (' + rsi.toFixed(0) + ')'; }
		criteria.push({ key: 'rsi', bias, label });
	}
	if (macd != null) {
		criteria.push({ key: 'macd', bias: macd.histogram >= 0 ? 1 : -1, label: macd.histogram >= 0 ? 'MACD bullisch' : 'MACD bärisch' });
	}
	if (supportDistPct != null && supportDistPct <= ANALYSIS.nearLevelPct) {
		criteria.push({ key: 'sr', bias: 1, label: 'nahe Unterstützung (' + formatLevel(nearestSupport) + ')' });
	} else if (resistanceDistPct != null && resistanceDistPct <= ANALYSIS.nearLevelPct) {
		criteria.push({ key: 'sr', bias: -1, label: 'nahe Widerstand (' + formatLevel(nearestResistance) + ')' });
	}
	// Candlestick-Muster der letzten Tageskerzen (Chat vom 2026-08-11).
	const candle = detectCandlePattern(chartData);
	if (candle) {
		criteria.push({ key: 'candle', bias: candle.bias, label: candle.label });
	}

	const score = criteria.reduce((sum, c) => sum + c.bias, 0);
	const macdBullCross = macd != null && macd.prevHistogram != null && macd.prevHistogram < 0 && macd.histogram >= 0;
	const macdBearCross = macd != null && macd.prevHistogram != null && macd.prevHistogram >= 0 && macd.histogram < 0;
	const bullTrigger = (rsi != null && rsi <= ANALYSIS.rsiBullTrigger)
		|| (supportDistPct != null && supportDistPct <= ANALYSIS.nearLevelPct)
		|| macdBullCross;
	const bearTrigger = (rsi != null && rsi >= ANALYSIS.rsiBearTrigger)
		|| (resistanceDistPct != null && resistanceDistPct <= ANALYSIS.nearLevelPct)
		|| macdBearCross;

	let verdict = 'neutral';
	if (score >= ANALYSIS.minScore && bullTrigger) verdict = 'long';
	else if (score <= -ANALYSIS.minScore && bearTrigger) verdict = 'short';

	return {
		ok: true, price, ema50, ema200, rsi, macd,
		supports, resistances, nearestSupport, nearestResistance,
		criteria, score, verdict,
	};
}

// Kurzfrist-Ampel fuer einen "absolut kurzfristigen" Trade (Chat vom
// 2026-08-10, Nutzerwunsch) - bewusst auf INTRADAY-Kerzen (z.B. 1-Minuten,
// Range "1m") gedacht, mit SCHNELLEN Indikatoren (EMA9/EMA21, RSI, MACD,
// Kurz-Momentum) statt der langsamen Tages-EMA50/200. Liefert eine Ampel:
//   'green'  = kurzfristig bullisch (spricht fuer Long)
//   'red'    = kurzfristig bärisch  (spricht fuer Short)
//   'yellow' = uneindeutig/neutral (abwarten)
// Reine Momentum-/Trendlogik (kein Ueberkauft/Ueberverkauft-Reversal wie bei
// analyzeSecurity) - fuer einen Scalp zaehlt die aktuelle Richtung.
export function analyzeShortTerm(chartData, livePrice = null) {
	const completed = (chartData || []).filter((row) => row && row.close != null);
	if (completed.length < 30) {
		return { ok: false };
	}
	// Laufende Minute als vorlaeufige letzte Kerze fuer die CLOSE-basierten
	// Indikatoren (EMA/RSI/MACD/Momentum) - so rechnet die App dieselbe "gerade
	// entstehende Minute" mit, die auch TradingViews Legende zeigt (Chat vom
	// 2026-08-11). Candlestick-Muster und S/R bleiben bewusst NUR auf
	// abgeschlossenen Kerzen (die laufende hat noch keine echten Hoch/Tief/
	// Pivots).
	const closes = (livePrice != null && Number.isFinite(livePrice))
		? [...completed, { close: livePrice }]
		: completed;
	const price = closes[closes.length - 1].close;
	const ema9 = lastEma(closes, 9);
	const ema21 = lastEma(closes, 21);
	const rsi = computeRsi(closes, 14);
	const macd = computeMacd(closes, 12, 26, 9);
	const lookback = Math.min(5, closes.length - 1);
	const momentum = price - closes[closes.length - 1 - lookback].close;

	const reasons = [];
	let score = 0;
	if (ema9 != null && ema21 != null) {
		const up = ema9 >= ema21;
		score += up ? 1 : -1;
		reasons.push({ bias: up ? 1 : -1, label: up ? 'EMA9 über EMA21' : 'EMA9 unter EMA21' });
	}
	if (ema21 != null) {
		const up = price >= ema21;
		score += up ? 1 : -1;
		reasons.push({ bias: up ? 1 : -1, label: up ? 'Kurs über EMA21' : 'Kurs unter EMA21' });
	}
	if (rsi != null) {
		const up = rsi >= 50;
		score += up ? 1 : -1;
		reasons.push({ bias: up ? 1 : -1, label: 'RSI ' + rsi.toFixed(0) });
	}
	if (macd != null) {
		const up = macd.histogram >= 0;
		score += up ? 1 : -1;
		reasons.push({ bias: up ? 1 : -1, label: up ? 'MACD bullisch' : 'MACD bärisch' });
	}
	const momUp = momentum >= 0;
	score += momUp ? 1 : -1;
	reasons.push({ bias: momUp ? 1 : -1, label: momUp ? 'Momentum aufwärts' : 'Momentum abwärts' });

	// Nächste Unterstützung/Widerstand (Chat vom 2026-08-11) - zum Anzeigen UND
	// als Gefahren-/Chancenpunkt: nahe am Widerstand ist ein Risiko fuer Long,
	// nahe an der Unterstuetzung ein Risiko fuer Short. Breiteres Pivot-Fenster
	// (10 statt 3) fuer die 1-Minuten-Daten - sonst erzeugt das Tick-Rauschen
	// bedeutungslose Mini-Level direkt am aktuellen Kurs.
	const { supports, resistances } = computeSupportResistance(completed, price, 3, 10);
	const nearestSupport = supports.length ? supports[0] : null;
	const nearestResistance = resistances.length ? resistances[0] : null;
	const supportDistPct = nearestSupport != null ? (price - nearestSupport) / price : null;
	const resistanceDistPct = nearestResistance != null ? (nearestResistance - price) / price : null;
	if (resistanceDistPct != null && resistanceDistPct <= ANALYSIS.nearLevelPctIntraday) {
		score -= 1;
		reasons.push({ bias: -1, label: 'dicht unter Widerstand (' + formatLevel(nearestResistance) + ')' });
	} else if (supportDistPct != null && supportDistPct <= ANALYSIS.nearLevelPctIntraday) {
		score += 1;
		reasons.push({ bias: 1, label: 'dicht über Unterstützung (' + formatLevel(nearestSupport) + ')' });
	}

	// Candlestick-Muster der letzten ABGESCHLOSSENEN 1-Minuten-Kerzen.
	const candle = detectCandlePattern(completed);
	if (candle) {
		score += candle.bias;
		reasons.push({ bias: candle.bias, label: candle.label });
	}

	const light = score >= 2 ? 'green' : score <= -2 ? 'red' : 'yellow';
	return { ok: true, light, score, reasons, nearestSupport, nearestResistance };
}

// Level-Zahl kompakt formatieren - Nachkommastellen abhaengig von der
// Groessenordnung (Cent-Werte brauchen mehr Stellen als 4-stellige Indizes).
export function formatLevel(value) {
	if (value == null) return '–';
	const abs = Math.abs(value);
	const digits = abs >= 1000 ? 0 : abs >= 10 ? 2 : abs >= 1 ? 2 : 4;
	return value.toLocaleString('de-DE', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}
